/* Created by Interactive 63 - https://interactive63.com */
/**
 * Diagnostic guides for a selected morph keyframe: the start points of the previous shape and
 * of this one as numbered dots, with lines joining a few corresponding points. A twisted morph
 * shows as lines that cross, which is the cue to change the start point.
 */
import { applyMatrix, type Matrix, type Point } from './geometry';
import type { MorphGuides } from './overlay';
import { buildMorphs } from '../../../core/morph/morph-track';
import { findKeyframe } from '../../../core/timeline/keyframes';
import type { ElementId } from '../../../core/model/types';
import type { Store } from '../../store/store';

const LINKS = 5;

/** What the guides need from the stage. */
export interface MorphGuideSource {
  store: Store;
  getElement(id: ElementId): Element | undefined;
  screenMatrix(node: SVGGraphicsElement): Matrix | null;
}

/** Guides for the single selected morph keyframe, or null when there is nothing to show. */
export function buildMorphGuides(source: MorphGuideSource): MorphGuides | null {
  const { project, editor } = source.store.getState();
  const [keyframeId] = editor.selection.keyframeIds;
  if (editor.selection.keyframeIds.length !== 1 || keyframeId === undefined) return null;
  const found = findKeyframe(project, keyframeId);
  if (found?.track.property !== 'morph' || found.index === 0) return null;
  const el = source.getElement(found.track.elementId);
  const matrix = el ? source.screenMatrix(el as SVGGraphicsElement) : null;
  if (!el || !matrix) return null;

  const build = buildMorphs(project, () => true).find((b) => b.track.id === found.track.id);
  if (!build?.result.ok) return null;
  const { frames, template } = build.result.data;
  const after = frames.findIndex((f) => f.time === found.keyframe.time);
  const afterFrame = frames[after];
  const beforeFrame = frames[after - 1];
  if (after < 1 || !afterFrame || !beforeFrame) return null;

  /** The start point of each subpath, and a few on-curve points along the first one. */
  const read = (values: readonly number[]): { starts: Point[]; along: Point[] } => {
    const starts: Point[] = [];
    const along: Point[] = [];
    let i = 0;
    template.subpaths.forEach((subpath, index) => {
      const start = applyMatrix(matrix, { x: values[i] as number, y: values[i + 1] as number });
      starts.push(start);
      if (index === 0) {
        for (let k = 0; k < LINKS; k += 1) {
          const seg = Math.floor(((k + 1) / LINKS) * subpath.segments) - 1;
          const at = i + 2 + Math.max(0, seg) * 6 + 4;
          along.push(applyMatrix(matrix, { x: values[at] as number, y: values[at + 1] as number }));
        }
      }
      i += 2 + subpath.segments * 6;
    });
    return { starts, along };
  };
  const a = read(beforeFrame.values);
  const b = read(afterFrame.values);
  return {
    before: a.starts,
    after: b.starts,
    links: a.along.map((point, k): [Point, Point] => [point, b.along[k] ?? point]),
  };
}
